/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseChannel } from '../models/api-response-channel';
import { ApiResponseListChannel } from '../models/api-response-list-channel';
import { ApiResponsePagedResultChannel } from '../models/api-response-paged-result-channel';
import { createChannel } from '../fn/channel-api/create-channel';
import { CreateChannel$Params } from '../fn/channel-api/create-channel';
import { deleteChannel } from '../fn/channel-api/delete-channel';
import { DeleteChannel$Params } from '../fn/channel-api/delete-channel';
import { getChannelByRef } from '../fn/channel-api/get-channel-by-ref';
import { GetChannelByRef$Params } from '../fn/channel-api/get-channel-by-ref';
import { listChannel } from '../fn/channel-api/list-channel';
import { ListChannel$Params } from '../fn/channel-api/list-channel';
import { searchChannel } from '../fn/channel-api/search-channel';
import { SearchChannel$Params } from '../fn/channel-api/search-channel';
import { updateArchiveStatus } from '../fn/channel-api/update-archive-status';
import { UpdateArchiveStatus$Params } from '../fn/channel-api/update-archive-status';
import { updateChannel } from '../fn/channel-api/update-channel';
import { UpdateChannel$Params } from '../fn/channel-api/update-channel';
import { updateChannelWithMessages } from '../fn/channel-api/update-channel-with-messages';
import { UpdateChannelWithMessages$Params } from '../fn/channel-api/update-channel-with-messages';

@Injectable({ providedIn: 'root' })
export class ChannelApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getChannelByRef()` */
  static readonly GetChannelByRefPath = '/v1/api/ui/channel/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getChannelByRef()` instead.
   *
   * This method doesn't expect any request body.
   */
  getChannelByRef$Response(params: GetChannelByRef$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseChannel>> {
    return getChannelByRef(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getChannelByRef$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getChannelByRef(params: GetChannelByRef$Params, context?: HttpContext): Observable<ApiResponseChannel> {
    return this.getChannelByRef$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseChannel>): ApiResponseChannel => r.body)
    );
  }

  /** Path part for operation `updateChannel()` */
  static readonly UpdateChannelPath = '/v1/api/ui/channel/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateChannel()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateChannel$Response(params: UpdateChannel$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseChannel>> {
    return updateChannel(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateChannel$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateChannel(params: UpdateChannel$Params, context?: HttpContext): Observable<ApiResponseChannel> {
    return this.updateChannel$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseChannel>): ApiResponseChannel => r.body)
    );
  }

  /** Path part for operation `deleteChannel()` */
  static readonly DeleteChannelPath = '/v1/api/ui/channel/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteChannel()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteChannel$Response(params: DeleteChannel$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteChannel(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteChannel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteChannel(params: DeleteChannel$Params, context?: HttpContext): Observable<void> {
    return this.deleteChannel$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `updateChannelWithMessages()` */
  static readonly UpdateChannelWithMessagesPath = '/v1/api/ui/channel/messages/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateChannelWithMessages()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateChannelWithMessages$Response(params: UpdateChannelWithMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseChannel>> {
    return updateChannelWithMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateChannelWithMessages$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateChannelWithMessages(params: UpdateChannelWithMessages$Params, context?: HttpContext): Observable<ApiResponseChannel> {
    return this.updateChannelWithMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseChannel>): ApiResponseChannel => r.body)
    );
  }

  /** Path part for operation `createChannel()` */
  static readonly CreateChannelPath = '/v1/api/ui/channel';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createChannel()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createChannel$Response(params: CreateChannel$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseChannel>> {
    return createChannel(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createChannel$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createChannel(params: CreateChannel$Params, context?: HttpContext): Observable<ApiResponseChannel> {
    return this.createChannel$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseChannel>): ApiResponseChannel => r.body)
    );
  }

  /** Path part for operation `searchChannel()` */
  static readonly SearchChannelPath = '/v1/api/ui/channel/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchChannel()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchChannel$Response(params: SearchChannel$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultChannel>> {
    return searchChannel(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchChannel$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchChannel(params: SearchChannel$Params, context?: HttpContext): Observable<ApiResponsePagedResultChannel> {
    return this.searchChannel$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultChannel>): ApiResponsePagedResultChannel => r.body)
    );
  }

  /** Path part for operation `listChannel()` */
  static readonly ListChannelPath = '/v1/api/ui/channel/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listChannel()` instead.
   *
   * This method doesn't expect any request body.
   */
  listChannel$Response(params: ListChannel$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListChannel>> {
    return listChannel(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listChannel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listChannel(params: ListChannel$Params, context?: HttpContext): Observable<ApiResponseListChannel> {
    return this.listChannel$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListChannel>): ApiResponseListChannel => r.body)
    );
  }

  /** Path part for operation `updateArchiveStatus()` */
  static readonly UpdateArchiveStatusPath = '/v1/api/ui/channel/archive/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateArchiveStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateArchiveStatus$Response(params: UpdateArchiveStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return updateArchiveStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateArchiveStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  updateArchiveStatus(params: UpdateArchiveStatus$Params, context?: HttpContext): Observable<void> {
    return this.updateArchiveStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
